import { formatDistanceToNow } from 'date-fns';
import {
  BookOpen,
  CheckCircle2,
  Rocket,
  AlertTriangle,
} from 'lucide-react';
import PageHeader from '@/components/common/PageHeader';
import MetricCard from '@/components/common/MetricCard';
import StatusBadge from '@/components/common/StatusBadge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorBanner from '@/components/common/ErrorBanner';
import { useServices } from '@/hooks/useServices';
import { useGitHubStats, useWorkflowRuns } from '@/hooks/useGitHub';
import { useDeployments } from '@/hooks/useDeployments';
import { useAlerts } from '@/hooks/useObservability';

export default function Dashboard() {
  const { data: servicesData, isLoading: servicesLoading } = useServices({ page: 1, page_size: 1 });
  const { data: githubStats, isLoading: statsLoading } = useGitHubStats();
  const { data: recentBuilds, isLoading: buildsLoading, error: buildsError, refetch: refetchBuilds } = useWorkflowRuns({ page: 1, page_size: 10 });
  const { data: recentDeploys, isLoading: deploysLoading } = useDeployments({ page: 1, page_size: 10 });
  const { data: alertsData, isLoading: alertsLoading } = useAlerts({ status: 'active', page: 1, page_size: 10 });

  const metricsLoading = servicesLoading || statsLoading || deploysLoading || alertsLoading;

  return (
    <div className="animate-slide-up">
      <PageHeader
        title="Dashboard"
        description="Overview of your platform's services, builds, and deployments."
      />

      {/* Top-level metric cards */}
      <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Total Services"
          value={metricsLoading ? '...' : servicesData?.total ?? 0}
          icon={<BookOpen className="h-5 w-5" />}
        />
        <MetricCard
          label="Build Success Rate (24h)"
          value={metricsLoading ? '...' : `${(githubStats?.success_rate_24h ?? 0).toFixed(1)}%`}
          icon={<CheckCircle2 className="h-5 w-5" />}
        />
        <MetricCard
          label="Deployments (24h)"
          value={metricsLoading ? '...' : githubStats?.total_workflow_runs_24h ?? 0}
          icon={<Rocket className="h-5 w-5" />}
        />
        <MetricCard
          label="Active Alerts"
          value={metricsLoading ? '...' : alertsData?.total ?? 0}
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Builds */}
        <div className="rounded-2xl border border-slate-200/60 bg-white shadow-card transition-all duration-200 hover:shadow-card-hover dark:border-slate-800/80 dark:bg-slate-900 dark:hover:border-slate-700">
          <div className="px-5 py-3.5 border-b border-slate-100/80 dark:border-slate-800/60">
            <h3 className="text-card-title text-slate-900 dark:text-slate-100">Recent Builds</h3>
          </div>
          <div>
            {buildsLoading ? (
              <div className="py-8"><LoadingSpinner size="sm" message="Loading builds..." /></div>
            ) : buildsError ? (
              <div className="p-4"><ErrorBanner message="Failed to load builds" onRetry={refetchBuilds} /></div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {(recentBuilds?.items ?? []).map((run) => (
                  <div key={run.id} className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-surface-50 dark:hover:bg-slate-800/30">
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                        {run.workflow_name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {run.repository_name ?? 'unknown'} / {run.head_branch ?? 'main'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={run.conclusion ?? run.status} />
                      <span className="text-xs text-slate-400 dark:text-slate-500">
                        {run.started_at
                          ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
                          : ''}
                      </span>
                    </div>
                  </div>
                ))}
                {(recentBuilds?.items ?? []).length === 0 && (
                  <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">No recent builds</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Deployments */}
        <div className="rounded-2xl border border-slate-200/60 bg-white shadow-card transition-all duration-200 hover:shadow-card-hover dark:border-slate-800/80 dark:bg-slate-900 dark:hover:border-slate-700">
          <div className="px-5 py-3.5 border-b border-slate-100/80 dark:border-slate-800/60">
            <h3 className="text-card-title text-slate-900 dark:text-slate-100">Recent Deployments</h3>
          </div>
          <div>
            {deploysLoading ? (
              <div className="py-8"><LoadingSpinner size="sm" message="Loading deployments..." /></div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {(recentDeploys?.items ?? []).map((deploy) => (
                  <div key={deploy.id} className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-surface-50 dark:hover:bg-slate-800/30">
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                        {deploy.service_name ?? deploy.service_slug ?? 'Unknown'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {deploy.environment_name ?? 'env'} {deploy.version ? `v${deploy.version}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={deploy.status} />
                      <span className="text-xs text-slate-400 dark:text-slate-500">
                        {formatDistanceToNow(new Date(deploy.started_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                ))}
                {(recentDeploys?.items ?? []).length === 0 && (
                  <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">No recent deployments</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active Alerts */}
      <div className="mt-8 rounded-2xl border border-slate-200/60 bg-white shadow-card transition-all duration-200 hover:shadow-card-hover dark:border-slate-800/80 dark:bg-slate-900 dark:hover:border-slate-700">
        <div className="px-5 py-3.5 border-b border-slate-100/80 dark:border-slate-800/60">
          <h3 className="text-card-title text-slate-900 dark:text-slate-100">Active Alerts</h3>
        </div>
        <div>
          {alertsLoading ? (
            <div className="py-8"><LoadingSpinner size="sm" message="Loading alerts..." /></div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {(alertsData?.items ?? []).map((alert) => (
                <div key={alert.id} className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-surface-50 dark:hover:bg-slate-800/30">
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{alert.title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {alert.service_slug ?? 'Platform'} - Started{' '}
                      {formatDistanceToNow(new Date(alert.alert_started_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={alert.status} />
                    {alert.priority && (
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                        {alert.priority}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {(alertsData?.items ?? []).length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 py-8">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-50 dark:bg-slate-800/30">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                  </div>
                  <span className="text-sm font-medium text-slate-400 dark:text-slate-500">All clear — no active alerts</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
